import { Button } from '@hologram-dimension/button';
import { Modal } from '@hologram-dimension/modal';
import clsx from 'clsx/lite';
import SimInventoryBulkUpdateState from 'components/SimInventoryBulkUpdateState/SimInventoryBulkUpdateState';
import { useCallback, useRef, useState, type FC } from 'react';
import type { HologramSimIdsMap, OnSubmit, SimInventoryBulkStateAction } from 'types/common';
import formatNumber from 'utils/numbers/formatNumber';
import type { CommonModalProps } from '../shared';
import styles from './SimInventoryBulkUpdateState.module.css';

interface SimInventoryBulkUpdateStateProps extends CommonModalProps {
  action: SimInventoryBulkStateAction;
  sims: HologramSimIdsMap;
  onSuccess: (sims: Array<HologramSimId>) => void;
}

const SimInventoryBulkUpdateStateModal: FC<SimInventoryBulkUpdateStateProps> = ({
  onClose,
  defaultOpen = false,
  className,
  action,
  sims,
  onSuccess,
}) => {
  // TODO: Consider moving SimInventoryBulkUpdateState component straight into this form to avoid all this extra state back and forth
  const [open, setOpen] = useState(defaultOpen);
  const [processing, setIsProcessing] = useState(false);
  const [previewDone, setPreviewDone] = useState(false);
  const [done, setDone] = useState(false);
  const [simCount, setSimCount] = useState(sims.size);
  const submitRef = useRef<OnSubmit>();

  const onProcessing = useCallback((value: boolean) => {
    setIsProcessing(value);
  }, []);

  const onPreviewDone = useCallback((validSimCount: number) => {
    setPreviewDone(true);
    setSimCount(validSimCount);
  }, []);

  const onDone = useCallback(
    (modifiedSims?: Array<HologramSimId>) => {
      setDone(true);
      if (modifiedSims) {
        onSuccess(modifiedSims);
      }
    },
    [onSuccess]
  );

  const handleSubmit: React.MouseEventHandler &
    React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };

  // No hooks after this, returns null for the modal for a few unexpected cases

  let actionText;

  if (sims.size === 0) {
    return null;
  }

  switch (action) {
    case 'pause':
      actionText = 'Pause';
      break;
    case 'resume':
      actionText = 'Resume';
      break;

    default:
      return null;
  }

  return (
    <Modal
      className={clsx(className, styles.wrapper)}
      header={`${actionText} data usage${
        previewDone && simCount > 1 ? ` for ${formatNumber(simCount)} SIMs` : ''
      }`}
      onClosed={onClose}
      open={open}
      setOpen={setOpen}
      noContentPadding
      footerActions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && previewDone && simCount > 0 && (
            <Button
              loading={processing}
              onClick={handleSubmit}
              variant={sims.size === 1 ? 'secondary' : 'primary'}
            >{`${actionText} ${simCount > 1 ? `${formatNumber(simCount)} SIMs` : 'SIM'}`}</Button>
          )}
        </>
      }
    >
      <SimInventoryBulkUpdateState
        action={action}
        sims={sims}
        onProcessing={onProcessing}
        ref={submitRef}
        onDone={onDone}
        onPreviewDone={onPreviewDone}
      />
    </Modal>
  );
};

export default SimInventoryBulkUpdateStateModal;
